import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    ChevronLeft, Building2, User, Calendar, Pencil, Trophy,
    Archive, ListTodo
} from 'lucide-react';
import api from '../api/api';
import PageShell from './ui/PageShell';
import { Button } from './ui/button';
import StatusBadge from './ui/StatusBadge';
import ScoreBadge from './ui/ScoreBadge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import EntityLinks from './EntityLinks';
import LoadingScreen from './ui/LoadingScreen';
import { SnackbarContext } from '../App';
import { formatCurrency, formatDate, formatDateTime, normalizeApiError } from '../lib/i18n';

const STAGES = ['NEW', 'QUALIFYING', 'MEETING', 'PROPOSAL', 'NEGOTIATION', 'WON', 'LOST'];
const STAGE_VARIANTS = {
    NEW: 'default',
    QUALIFYING: 'indigo',
    MEETING: 'purple',
    PROPOSAL: 'warning',
    NEGOTIATION: 'blue',
    WON: 'success',
    LOST: 'destructive'
};
const STATUS_VARIANTS = {
    OPEN: 'blue',
    WON: 'success',
    LOST: 'destructive',
    ARCHIVED: 'default'
};

export default function OpportunityDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const showSnackbar = useContext(SnackbarContext);
    const { t, i18n } = useTranslation();

    const [loading, setLoading] = useState(true);
    const [oppDetail, setOppDetail] = useState(null);

    const fetchOppDetail = useCallback(async () => {
        try {
            setLoading(true);
            const res = await api.get(`/api/opportunities/${id}`);
            setOppDetail(res.data);
        } catch (err) {
            console.error('Fetch detail error', err);
            showSnackbar(normalizeApiError(err, t, 'opportunityDetail.messages.loadFailed'), 'error');
            navigate('/opportunities');
        } finally {
            setLoading(false);
        }
    }, [id, navigate, showSnackbar, t]);

    useEffect(() => {
        if (id) fetchOppDetail();
    }, [id, fetchOppDetail]);

    const handleUpdateStage = async (oppId, newStage) => {
        try {
            await api.put(`/api/opportunities/${oppId}`, { stage: newStage });
            showSnackbar(t('opportunityDetail.messages.stageMoved', { stage: newStage }), 'success');
            fetchOppDetail();
        } catch (err) {
            showSnackbar(normalizeApiError(err, t, 'opportunityDetail.messages.stageFailed'), 'error');
        }
    };

    const handleUpdateStatus = async (newStatus) => {
        try {
            await api.put(`/api/opportunities/${id}`, { status: newStatus });
            showSnackbar(t('opportunityDetail.messages.statusUpdated', { status: newStatus }), 'success');
            fetchOppDetail();
        } catch (err) {
            showSnackbar(normalizeApiError(err, t, 'opportunityDetail.messages.statusFailed'), 'error');
        }
    };

    const handleArchive = async (oppId) => {
        try {
            await api.delete(`/api/opportunities/${oppId}`);
            showSnackbar(t('opportunityDetail.messages.archived'), 'success');
            navigate('/opportunities');
        } catch (err) {
            showSnackbar(normalizeApiError(err, t, 'opportunityDetail.messages.archiveFailed'), 'error');
        }
    };

    const handleCreateFollowUpTask = () => {
        showSnackbar(t('opportunityDetail.messages.taskSyncInDevelopment'), 'default');
    };

    if (loading) return <LoadingScreen />;
    if (!oppDetail) return null;

    return (
        <PageShell>
            <div className="max-w-7xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
                {/* Header / Navigation */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 bg-card/40 backdrop-blur-sm p-8 rounded-[2.5rem] border border-border shadow-sm">
                    <div className="flex items-center gap-6">
                        <Button variant="ghost" size="icon" onClick={() => navigate('/opportunities')}
                            className="h-14 w-14 rounded-2xl hover:bg-muted/50 transition-all border border-transparent hover:border-border">
                            <ChevronLeft className="w-6 h-6" />
                        </Button>
                        <div className="space-y-1.5">
                            <div className="flex items-center gap-3">
                                <h1 className="text-3xl font-black text-foreground tracking-tight">{oppDetail.opportunity.title}</h1>
                                <div className="flex items-center gap-2">
                                    <StatusBadge variant={STAGE_VARIANTS[oppDetail.opportunity.stage]}>{oppDetail.opportunity.stage}</StatusBadge>
                                    {oppDetail.opportunity.status !== 'OPEN' && <StatusBadge variant={STATUS_VARIANTS[oppDetail.opportunity.status]}>{oppDetail.opportunity.status}</StatusBadge>}
                                </div>
                            </div>
                            <div className="flex items-center gap-3 text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-60">
                                <Building2 className="w-3.5 h-3.5" />
                                <span>{oppDetail.opportunity.companyId?.name || oppDetail.opportunity.companyMasterId?.name || t('opportunityDetail.common.unassignedCompany')}</span>
                                <span className="opacity-30">•</span>
                                <Calendar className="w-3.5 h-3.5" />
                                <span>{t('opportunityDetail.common.created', { date: formatDate(oppDetail.opportunity.createdAt, { dateStyle: 'medium' }, i18n.language) })}</span>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <Button variant="outline" onClick={() => navigate(`/opportunities/${id}/edit`)}
                            className="h-12 px-6 rounded-2xl border-2 border-border/50 font-black uppercase tracking-widest text-[10px] gap-2 hover:bg-muted/30 transition-all">
                            <Pencil className="w-4 h-4" /> {t('opportunityDetail.actions.editDetail')}
                        </Button>
                        <Button className="bg-primary text-primary-foreground h-12 px-8 rounded-2xl font-black uppercase tracking-widest text-[10px] shadow-xl hover:bg-primary/90 transition-all active:scale-95">
                            {t('opportunityDetail.actions.addActivity')}
                        </Button>
                    </div>
                </div>

                {/* Related Entity Links */}
                <div className="mb-6">
                    <EntityLinks
                        company={oppDetail.opportunity.companyId || oppDetail.opportunity.companyMasterId}
                        contact={oppDetail.opportunity.primaryContactId}
                        onNavigate={(type, targetId) => navigate(type === 'contact' ? `/crm/contacts/${targetId}` : `/crm/companies/${targetId}`)}
                    />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Left: Overview + Activity */}
                    <div className="lg:col-span-2 space-y-8">
                        {/* Overview Card */}
                        <div className="bg-card shadow-sm rounded-[2.5rem] p-8 border border-border group hover:border-primary/20 transition-all duration-500">
                            <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground mb-8 pb-4 border-b border-border/50">{t('opportunityDetail.sections.dealOverview')}</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
                                <div className="flex flex-col">
                                    <p className="text-[9px] uppercase tracking-widest text-muted-foreground font-black mb-2 opacity-50">{t('opportunityDetail.sections.expectedValue')}</p>
                                    <p className="text-3xl font-black text-foreground tabular-nums tracking-tight">
                                        {formatCurrency(oppDetail.opportunity.expectedValue, oppDetail.opportunity.currency, {}, i18n.language)}
                                    </p>
                                </div>
                                <div className="flex flex-col">
                                    <p className="text-[9px] uppercase tracking-widest text-muted-foreground font-black mb-2 opacity-50">{t('opportunityDetail.sections.closingWindow')}</p>
                                    <p className="text-lg font-black text-foreground flex items-center gap-2.5">
                                        <Calendar className="w-5 h-5 text-primary" />
                                        {oppDetail.opportunity.expectedCloseDate ? formatDate(oppDetail.opportunity.expectedCloseDate, { dateStyle: 'medium' }, i18n.language) : t('opportunityDetail.common.openEnded')}
                                    </p>
                                </div>
                                <div className="flex flex-col">
                                    <p className="text-[9px] uppercase tracking-widest text-muted-foreground font-black mb-2 opacity-50">{t('opportunityDetail.sections.territoryOwner')}</p>
                                    <div className="text-lg font-black text-foreground flex items-center gap-2.5">
                                        <div className="w-8 h-8 rounded-xl bg-primary/10 flex items-center justify-center text-primary border border-primary/20">
                                            <User className="w-4 h-4" />
                                        </div>
                                        {oppDetail.opportunity.ownerId?.name || oppDetail.opportunity.ownerId?.email || t('opportunityDetail.common.systemAccount')}
                                    </div>
                                </div>
                                {oppDetail.opportunity.source && (
                                    <div className="flex flex-col">
                                        <p className="text-[9px] uppercase tracking-widest text-muted-foreground font-black mb-2 opacity-50">{t('opportunityDetail.sections.acquisitionSource')}</p>
                                        <div className="flex">
                                            <StatusBadge variant="indigo" className="font-black px-3 py-1">{oppDetail.opportunity.source}</StatusBadge>
                                        </div>
                                    </div>
                                )}
                                {oppDetail.opportunity.notes && (
                                    <div className="sm:col-span-2 lg:col-span-3">
                                        <p className="text-[9px] uppercase tracking-widest text-muted-foreground font-black mb-3 opacity-50">{t('opportunityDetail.sections.notes')}</p>
                                        <div className="bg-muted/30 p-5 rounded-2xl border border-border/50 text-sm font-bold text-muted-foreground/80 leading-relaxed whitespace-pre-wrap italic">
                                            "{oppDetail.opportunity.notes}"
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Activity History */}
                        <div className="bg-card shadow-sm rounded-3xl p-6 border border-border">
                            <h3 className="text-sm font-black uppercase tracking-widest text-muted-foreground mb-5">{t('opportunityDetail.sections.activityHistory')}</h3>
                            {oppDetail.activities.length === 0 ? (
                                <div className="text-center py-10">
                                    <p className="text-sm text-muted-foreground font-medium">{t('opportunityDetail.empty.noActivity')}</p>
                                </div>
                            ) : (
                                <div className="relative border-l-2 border-border ml-3 space-y-6 pb-2">
                                    {oppDetail?.activities?.map((act) => (
                                        <div key={act._id} className="relative pl-6">
                                            <div className="absolute w-3 h-3 bg-primary rounded-full -left-[7px] top-1.5 ring-4 ring-background border border-primary/30"></div>
                                            <div className="flex justify-between items-start mb-1">
                                                <StatusBadge variant={act.type === 'STAGE_CHANGE' ? 'indigo' : act.type === 'VALUE_CHANGE' ? 'warning' : act.type === 'STATUS_CHANGE' ? 'purple' : 'default'}>
                                                    {act.type.replace(/_/g, ' ')}
                                                </StatusBadge>
                                                <span className="text-[10px] text-muted-foreground font-medium">{formatDateTime(act.createdAt, {}, i18n.language)}</span>
                                            </div>
                                            {act.type === 'STAGE_CHANGE' && (
                                                <p className="text-sm text-muted-foreground mt-1">{t('opportunityDetail.activity.stageChanged', { oldValue: String(act.oldValue), newValue: String(act.newValue) })}</p>
                                            )}
                                            {act.type === 'VALUE_CHANGE' && (
                                                <p className="text-sm text-muted-foreground mt-1">{t('opportunityDetail.activity.valueChanged', { oldValue: formatCurrency(act.oldValue, 'USD', {}, i18n.language), newValue: formatCurrency(act.newValue, 'USD', {}, i18n.language) })}</p>
                                            )}
                                            {act.type === 'STATUS_CHANGE' && (
                                                <p className="text-sm text-muted-foreground mt-1">{t('opportunityDetail.activity.statusChanged', { newValue: String(act.newValue) })}</p>
                                            )}
                                            {act.notes && <p className="mt-2 text-sm text-muted-foreground italic bg-muted/20 p-2.5 rounded-xl border border-border/30">"{act.notes}"</p>}
                                            <p className="text-[10px] text-muted-foreground mt-2 font-medium">by {act.userId?.name || act.userId?.email || 'System'}</p>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right Sidebar */}
                    <div className="space-y-8">
                        {/* Intelligence */}
                        <div className="bg-card shadow-sm rounded-[2.5rem] p-8 border border-border">
                            <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground mb-6">AI Intelligence</h3>
                            <div className="space-y-6">
                                <div className="flex items-center justify-between p-4 bg-muted/20 border border-border/50 rounded-2xl">
                                    <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Deal Quality</span>
                                    <div className="scale-110">
                                        <ScoreBadge intelligence={oppDetail?.intelligence?.opportunity} label="Deal" />
                                    </div>
                                </div>
                                {oppDetail?.intelligence?.account && (
                                    <div className="flex items-center justify-between p-4 bg-muted/20 border border-border/50 rounded-2xl">
                                        <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Account Health</span>
                                        <div className="scale-110">
                                            <ScoreBadge intelligence={oppDetail.intelligence.account} label="Account" />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Pipeline Management */}
                        <div className="bg-card shadow-sm rounded-[2.5rem] p-8 border border-border">
                            <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground mb-6">Pipeline Logic</h3>
                            <div className="space-y-4">
                                <div className="p-5 border-2 border-border/50 rounded-2xl bg-muted/5 group focus-within:border-primary transition-all">
                                    <label className="text-[9px] font-black uppercase tracking-[0.15em] text-primary block mb-3 opacity-70">Advance Stage</label>
                                    <Select value={oppDetail?.opportunity?.stage || 'NEW'} onValueChange={(val) => handleUpdateStage(oppDetail?.opportunity?._id, val)}>
                                        <SelectTrigger className="w-full h-11 bg-background border-2 border-border/50 text-[11px] font-black uppercase tracking-widest rounded-xl px-4 focus:ring-primary focus:border-primary">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="rounded-2xl border-border shadow-2xl">
                                            {STAGES.map(s => <SelectItem key={s} value={s} className="text-xs font-bold">{s}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                    <Button variant="outline" className="h-14 rounded-2xl font-black uppercase tracking-widest text-[9px] gap-2 border-border/50 hover:bg-success/5 hover:border-success/30 hover:text-success transition-all"
                                        onClick={() => handleUpdateStatus('WON')}>
                                        <Trophy className="w-4 h-4" /> Lost? Won!
                                    </Button>
                                    <Button variant="outline" className="h-14 rounded-2xl font-black uppercase tracking-widest text-[9px] gap-2 border-border/50 hover:bg-destructive/5 hover:border-destructive/30 hover:text-destructive transition-all"
                                        onClick={() => handleUpdateStatus('LOST')}>
                                        <Archive className="w-4 h-4" /> Lost Case
                                    </Button>
                                </div>

                                {oppDetail.opportunity.status !== 'ARCHIVED' && (
                                    <Button variant="outline" className="w-full h-12 rounded-2xl font-black uppercase tracking-widest text-[9px] gap-2 border-border/50 text-muted-foreground hover:bg-muted/30 transition-all"
                                        onClick={() => handleArchive(oppDetail.opportunity._id)}>
                                        <Archive className="w-4 h-4" /> Archive Forever
                                    </Button>
                                )}

                                <Button variant="outline" className="w-full h-12 rounded-2xl font-black uppercase tracking-widest text-[9px] gap-2 bg-primary/5 border-primary/20 text-primary hover:bg-primary/10 transition-all"
                                    onClick={handleCreateFollowUpTask}>
                                    <ListTodo className="w-4 h-4" /> New Task Link
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </PageShell>
    );
}
